import React, { useState } from 'react';
import { RefreshCw, Clock, Settings, X, Check, Sun, Moon } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const [showSettings, setShowSettings] = useState(false);
  const { 
    selectedCurrency, 
    setSelectedCurrency, 
    lastUpdate, 
    isUpdating, 
    currencyConfig,
    apiStatus,
    refreshData 
  } = useCurrency();
  const { isDark, toggleTheme, theme } = useTheme();

  const currencies = ['USD', 'EUR', 'TRY', 'AED'];

  return (
    <header 
      className="sticky top-0 z-50 border-b transition-colors duration-300"
      style={{ backgroundColor: theme.bgTertiary, borderColor: theme.border }}
    >
      <div className="max-w-full px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#d4af37] to-[#b8960c] rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-black font-bold text-lg">L</span>
              </div>
              <div>
                <h1 className="text-[#d4af37] font-bold text-xl tracking-wide">LETSGOLD.IO</h1>
                <p style={{ color: theme.textMuted }} className="text-xs">Live Prices</p>
              </div>
            </div>
          </div>

          {/* Center - Update Status */}
          <div className="hidden md:flex items-center gap-6">
            <div className="flex items-center gap-2 text-sm" style={{ color: theme.textSecondary }}>
              <Clock className="w-4 h-4" />
              <span>Last Update: {lastUpdate.toLocaleTimeString('en-US')}</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              isUpdating ? 'bg-green-900/30 text-green-400' : ''
            }`} style={!isUpdating ? { backgroundColor: theme.bgHover, color: theme.textSecondary } : {}}>
              <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
              <span className="text-xs font-medium">
                {isUpdating ? 'Updating...' : 'Live'}
              </span>
            </div>
            {/* API Status */}
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${apiStatus.gold ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <span className="text-xs" style={{ color: theme.textMuted }}>API</span>
            </div>
          </div>

          {/* Right - Theme Toggle & Settings */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: theme.bgHover }}
              title={isDark ? 'Light Mode' : 'Dark Mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600" />
              )}
            </button>

            {/* Currency Badge */}
            <div 
              className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{ backgroundColor: theme.bgHover }}
            >
              <span className="text-[#d4af37] font-bold">{currencyConfig[selectedCurrency]?.symbol}</span>
              <span className="text-sm font-medium" style={{ color: theme.text }}>{selectedCurrency}</span>
            </div>

            <button 
              onClick={() => setShowSettings(!showSettings)}
              className="flex items-center gap-2 px-4 py-2 bg-[#d4af37] hover:bg-[#c9a430] text-black font-semibold rounded-lg transition-all duration-200 text-sm"
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline">Settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Settings Dropdown */}
      {showSettings && (
        <div 
          className="absolute right-4 top-16 rounded-xl shadow-2xl p-4 min-w-[280px] z-50 border"
          style={{ backgroundColor: theme.bgTertiary, borderColor: theme.border }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ color: theme.text }}>Select Currency</h3>
            <button 
              onClick={() => setShowSettings(false)}
              className="transition-colors" 
              style={{ color: theme.textSecondary }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-2">
            {currencies.map(currency => (
              <button
                key={currency}
                onClick={() => {
                  setSelectedCurrency(currency);
                  setShowSettings(false);
                }}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 border ${
                  selectedCurrency === currency 
                    ? 'bg-[#d4af37]/20 border-[#d4af37]' 
                    : 'border-transparent'
                }`}
                style={selectedCurrency !== currency ? { backgroundColor: theme.bgHover } : {}}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl w-8 text-center">{currencyConfig[currency]?.symbol}</span>
                  <div className="text-left">
                    <p className="font-medium" style={{ color: theme.text }}>{currency}</p>
                    <p className="text-xs" style={{ color: theme.textMuted }}>{currencyConfig[currency]?.name}</p>
                  </div>
                </div>
                {selectedCurrency === currency && (
                  <Check className="w-5 h-5 text-[#d4af37]" />
                )}
              </button>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.border }}>
            <button
              onClick={() => {
                refreshData();
                setShowSettings(false);
              }}
              className="w-full flex items-center justify-center gap-2 p-2 rounded-lg transition-colors text-sm"
              style={{ backgroundColor: theme.bgHover, color: theme.text }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Data
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
